'use client';
import { AlertTriangle, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { analytics } from '@/lib/analytics';
import { Button } from '../Button/Button';
import { toWarningText } from '@/utils/mod/check';

interface UgcWarningProps {
  hasTags: boolean;
  hasMakes: boolean;
  description: string | null;
  hasImage: boolean;
}

const UgcWarning = ({ hasTags, hasMakes, description, hasImage }: UgcWarningProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      analytics.capture('tone.ugc_warning_modal.opened');
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const warnings = [];
  if (!hasImage) warnings.push('photo');
  if ((description?.length ?? 0) < 100)
    warnings.push('detailed description (minimum 100 characters)');
  if (!hasTags) warnings.push('tags');
  if (!hasMakes) warnings.push('makes and models');

  if (warnings.length === 0) return null;

  return (
    <>
      <button
        className="bg-zinc-900 w-full text-white p-2 flex items-center justify-center gap-4 mt-[-1px]"
        onClick={toggleModal}
      >
        <div className="text-sm">
          <AlertTriangle className="w-6 h-6 text-[#FFFF00]" />
        </div>
        <div className="text-base font-semibold">Limited Reach</div>
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="flex flex-row items-center gap-4">
                <AlertTriangle className="w-6 h-6 text-white" />
                <span className="text-xl font-semibold">Limited Reach</span>
              </span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="p-4 flex flex-col gap-6">
              <span className="text-zinc-400 text-left">{toWarningText(warnings)}</span>
              <span className={'flex flex-row gap-4'}>
                <Button
                  className={'w-full'}
                  onClick={toggleModal}
                  isPrimary={true}
                  variant={'slim'}
                >
                  Close
                </Button>
              </span>
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export default UgcWarning;
